import React, { useState, useEffect } from 'react'
import {
    Box,
    SpaceBetween,
    Toggle,
    DatePicker,
    FormField,
    Select,
    Multiselect,
    SelectProps,
    Alert,
} from '@amzn/awsui-components-react'
import Textarea from '@amzn/awsui-components-react/polaris/textarea'
import ModalTemplate from '../reusable/ModalTemplate'
import { ModalModes } from '../../Constant'
import { useAppContext } from '../../../context'
import {
    BANNER_TYPE,
    BANNER_TYPE_OPTIONS,
    convertMilliToSecEpochTime,
    EMPTY_BANNER_EXPIRATION_DATE,
    EMPTY_BANNER_MESSAGE,
    getDefaultExpirationDate,
    isDateEnabled,
} from './BannerUtil'
import { generateId } from '../../common/Util'
import useStore from '../../Store'
// todo - tinymce integration (opensource version) - deprecating licensed versioned
const CreateEditBannerModal = ({
    mode,
    modalVisible,
    setModalVisible,
    selectedBanner,
    setSelectedBanner,
    getBanners,
    bannerAlerts,
    setBannerAlerts,
    dismissAlerts,
    refreshAppBanners,
}) => {
    const appContext = useAppContext()
    const apiClient = appContext.apiClient
    const businessEntitiesFromStore = useStore((state) => state.businessEntities)
    const selectBusinessEntity = useStore((state) => state.selectBusinessEntity)
    const [isRemovable, setIsRemovable] = useState<boolean>(true)
    const [bannerExpirationDate, setBannerExpirationDate] = useState<string>(
        EMPTY_BANNER_EXPIRATION_DATE,
    )
    const [bannerType, setBannerType] = useState<SelectProps.Option>(BANNER_TYPE_OPTIONS[0])
    const [bannerMessage, setBannerMessage] = useState<string>(EMPTY_BANNER_MESSAGE)
    const [isFalcon, setIsFalcon] = useState<boolean>(true)
    const [isEgret, setIsEgret] = useState<boolean>(false)
    const [showForAllBusinessEntities, setShowForAllBusinessEntities] = useState<boolean>(true)
    const [selectedBusinessEntities, setSelectedBusinessEntities] = useState<SelectProps.Option[]>(
        [],
    )
    const [businessEntityOptions, setBusinessEntityOptions] = useState<SelectProps.Option[]>([])
    const [validationError, setValidationError] = useState<string | null>(null)

    // Update business entity options when store data changes
    useEffect(() => {
        setBusinessEntityOptions(
            businessEntitiesFromStore.map((businessEntity) => ({
                value: businessEntity.business_entity_id,
                label: businessEntity.business_entity_name,
            })),
        )
    }, [businessEntitiesFromStore])

    // Helper function to convert selected business entities to list
    const getBusinessEntityIds = (): string[] => {
        if (showForAllBusinessEntities) {
            return []
        }
        return selectedBusinessEntities.map((option) => option.value as string)
    }

    const validateBannerPayload = (payload) => {
        const hasMessageAndType =
            payload.message !== EMPTY_BANNER_MESSAGE && payload.banner_type !== BANNER_TYPE.NOT_SET
        const hasValidBusinessEntities =
            showForAllBusinessEntities || selectedBusinessEntities.length > 0
        return hasMessageAndType && hasValidBusinessEntities
    }

    const getDisabledMessage = () => {
        if (bannerMessage === EMPTY_BANNER_MESSAGE || !bannerMessage.trim()) {
            return 'Please enter a banner message'
        }

        if (bannerType.value === BANNER_TYPE.NOT_SET) {
            return 'Please select a banner type'
        }

        if (!showForAllBusinessEntities && selectedBusinessEntities.length === 0) {
            return 'Please select at least one business entity'
        }

        return null
    }

    const generateBannerPayload = () => {
        const bannerId =
            selectedBanner && mode === ModalModes.EDIT ? selectedBanner.value : generateId()
        const date = bannerExpirationDate ? bannerExpirationDate : getDefaultExpirationDate()
        const dateSplit: string[] = date.split('-')
        return {
            banner_id: bannerId,
            ttl: convertMilliToSecEpochTime(
                new Date(
                    parseInt(dateSplit[0]),
                    parseInt(dateSplit[1]) - 1,
                    parseInt(dateSplit[2]),
                ).valueOf(),
            ),
            banner_type: bannerType.value,
            message: bannerMessage,
            removable: isRemovable,
            is_falcon: isFalcon,
            is_egret: isEgret,
            cross_business_entity: showForAllBusinessEntities,
            business_entities: getBusinessEntityIds(),
        }
    }
    const onBannerSubmitHandler = () => {
        const payload = generateBannerPayload()

        // Check validation before submitting
        if (!validateBannerPayload(payload)) {
            setValidationError(getDisabledMessage())
            return
        }

        // Clear any existing validation error
        setValidationError(null)

        createBanner(payload)
            .then(() => {
                getBanners()
                // Also refresh app-wide banners
                if (refreshAppBanners) {
                    refreshAppBanners()
                }
            })
            .catch((error) => {
                console.error(error)
            })
        setModalVisible(false)
        setBannerExpirationDate(EMPTY_BANNER_EXPIRATION_DATE)
        setBannerType(BANNER_TYPE_OPTIONS[0])
        setBannerMessage('')
        setIsRemovable(true)
        setShowForAllBusinessEntities(true)
        setIsFalcon(true)
        setIsEgret(false)
        setSelectedBusinessEntities([])
        setSelectedBanner([])
    }

    const createBanner = (payload) => {
        return apiClient.put(`/banner/${payload.banner_id}`, payload)
    }

    useEffect(() => {
        if (!modalVisible) {
            setBannerExpirationDate(EMPTY_BANNER_EXPIRATION_DATE)
            setBannerType(BANNER_TYPE_OPTIONS[0])
            setBannerMessage(EMPTY_BANNER_MESSAGE)
            setIsRemovable(true)
            setShowForAllBusinessEntities(true)
            setIsFalcon(true)
            setIsEgret(false)
            setSelectedBusinessEntities([])
            setSelectedBanner([])
            return
        }
        if (selectedBanner) {
            const foundBannerType = BANNER_TYPE_OPTIONS.find(
                (option) => option.value === selectedBanner.banner_type,
            )
            setBannerType(foundBannerType || BANNER_TYPE_OPTIONS[0])
            const selectedDate = new Date(selectedBanner.ttl * 1000).toISOString().split('-')
            const month = selectedDate[1]
            const day = selectedDate[2].split('T')[0]
            setBannerExpirationDate(`${selectedDate[0]}-${month}-${day}`)
            setBannerMessage(selectedBanner.message)
            setIsRemovable(selectedBanner.removable)

            // Set the new fields if they exist in the selected banner
            if (typeof selectedBanner.cross_business_entity === 'boolean') {
                setShowForAllBusinessEntities(selectedBanner.cross_business_entity)
            }
            if (typeof selectedBanner.is_falcon === 'boolean') {
                setIsFalcon(selectedBanner.is_falcon)
            }
            if (typeof selectedBanner.is_egret === 'boolean') {
                setIsEgret(selectedBanner.is_egret)
            }
            if (
                selectedBanner.business_entities &&
                Array.isArray(selectedBanner.business_entities)
            ) {
                const selectedOptions = businessEntityOptions.filter((option) =>
                    selectedBanner.business_entities.includes(option.value),
                )
                setSelectedBusinessEntities(selectedOptions)
            }
        }
    }, [modalVisible, businessEntityOptions])

    return (
        <ModalTemplate
            modalVisible={modalVisible}
            onModalVisibleChange={setModalVisible}
            title={mode === ModalModes.CREATE ? 'Create Banner' : 'Edit Banner'}
            body={
                <SpaceBetween size='m' direction={'vertical'}>
                    {validationError && (
                        <Alert type='error' dismissible onDismiss={() => setValidationError(null)}>
                            {validationError}
                        </Alert>
                    )}
                    <FormField label='Message'>
                        <Box>
                            {/*TODO: replace with tinymce text editor */}
                            <Textarea
                                onChange={({ detail }) => setBannerMessage(detail.value)}
                                value={bannerMessage}
                                placeholder='Enter banner message ...'
                            />
                        </Box>
                    </FormField>
                    <FormField label='Type'>
                        <Box>
                            <Select
                                selectedOption={bannerType}
                                onChange={({ detail }) => setBannerType(detail.selectedOption)}
                                options={BANNER_TYPE_OPTIONS}
                            />
                        </Box>
                    </FormField>
                    <SpaceBetween size='s' direction='horizontal'>
                        <FormField label={'Is Banner Removable?'}></FormField>
                        <Toggle
                            onChange={({ detail }) => setIsRemovable(detail.checked)}
                            checked={isRemovable}
                        ></Toggle>
                    </SpaceBetween>
                    <SpaceBetween size='s' direction='horizontal'>
                        <FormField label={'Show for all business entities?'}></FormField>
                        <Toggle
                            onChange={({ detail }) => {
                                setShowForAllBusinessEntities(detail.checked)

                                // When toggling off "show for all", pre-select current business entity
                                // only if no business entities are already selected
                                if (
                                    !detail.checked &&
                                    selectedBusinessEntities.length === 0 &&
                                    selectBusinessEntity.id
                                ) {
                                    const currentBEOption = businessEntityOptions.find(
                                        (option) => option.value === selectBusinessEntity.id,
                                    )
                                    if (currentBEOption) {
                                        setSelectedBusinessEntities([currentBEOption])
                                    }
                                }
                            }}
                            checked={showForAllBusinessEntities}
                        ></Toggle>
                    </SpaceBetween>
                    {!showForAllBusinessEntities && (
                        <FormField label='Business Entities'>
                            <Box>
                                <Multiselect
                                    selectedOptions={selectedBusinessEntities}
                                    onChange={({ detail }) =>
                                        setSelectedBusinessEntities([...detail.selectedOptions])
                                    }
                                    options={businessEntityOptions}
                                    placeholder='Select business entities...'
                                />
                            </Box>
                        </FormField>
                    )}
                    <SpaceBetween size='s' direction='horizontal'>
                        <FormField label={'Is Falcon?'}></FormField>
                        <Toggle
                            onChange={({ detail }) => setIsFalcon(detail.checked)}
                            checked={isFalcon}
                        ></Toggle>
                    </SpaceBetween>
                    <SpaceBetween size='s' direction='horizontal'>
                        <FormField label={'Is Egret?'}></FormField>
                        <Toggle
                            onChange={({ detail }) => setIsEgret(detail.checked)}
                            checked={isEgret}
                        ></Toggle>
                    </SpaceBetween>
                    <FormField label='Banner Expiration Date'>
                        <DatePicker
                            isDateEnabled={(date: Date) => {
                                return isDateEnabled(date)
                            }}
                            onChange={({ detail }) => setBannerExpirationDate(detail.value)}
                            value={bannerExpirationDate}
                            openCalendarAriaLabel={(selectedDate) =>
                                'Choose Banner Expiration Date' +
                                (selectedDate
                                    ? `, selected date is ${selectedDate}`
                                    : EMPTY_BANNER_EXPIRATION_DATE)
                            }
                            nextMonthAriaLabel='Next month'
                            placeholder='YYYY/MM/DD'
                            previousMonthAriaLabel='Previous month'
                            todayAriaLabel='Today'
                        />
                    </FormField>
                </SpaceBetween>
            }
            actionName={mode === ModalModes.CREATE ? 'Submit' : 'Update'}
            action={() => {
                onBannerSubmitHandler()
            }}
            onDismiss={() => {
                setModalVisible(false)
                setSelectedBanner([])
            }}
        />
    )
}

export default CreateEditBannerModal
